<template>
	<div v-if="items?.length" class="flex flex-wrap relative flex-1">
		<div
			class="grid grid-cols-2 md:grid-cols-4 gap-4 w-full transition-opacity"
			:class="!isLoaded ? 'opacity-50' : ''"
		>
			<template v-for="(item, index) in items" :key="item.id">
				<template v-if="item.type === 'product'">
					<template v-if="index < 2">
						<ProductCard
							:product="item.data"
							:picture-size="`w-full h-auto`"
							:picture-aspect-ratio="pictureAspectRatio"
							class="min-h-[300px]"
							:lazy-loading="false"
							:special-price="specialPrice"
						/>
					</template>
					<template v-else>
						<LazyLoadWrapper>
							<ProductCard
								:product="item.data"
								:picture-size="`w-full h-auto`"
								:picture-aspect-ratio="pictureAspectRatio"
								class="min-h-[300px]"
								:lazy-loading="true"
								:special-price="specialPrice"
							/>
						</LazyLoadWrapper>
					</template>
				</template>
				<template v-else-if="item.type === 'advertisement'">
					<StoryblokComponent
						:blok="item.data"
						:is-category="item.data.component === 'Hero' ? true : null"
						:class="item.isFullWidth ? 'col-span-2' : ''"
					/>
				</template>
			</template>
		</div>
		<LazyLoadWrapper>
			<div v-if="isPagination" class="mt-8 w-full">
				<div class="py-5 w-full flex justify-center border-y border-gray-300">
					<Pagination
						:page-size="+pageSize"
						:total-items="itemCount"
						:current-page="currentPage"
						@get-current-page-products="handleGetCurrentPageProducts"
					/>
				</div>
			</div>
			<CategoryPagination
				v-if="!isPagination"
				:current-page="currentPage"
				:item-count="itemCount"
				:page-size="pageSize"
			/>
		</LazyLoadWrapper>
	</div>
	<div
		v-else
		class="flex flex-wrap relative flex-1 items-center justify-center min-h-[400px]"
	>
		<span class="bg-gray-lighter p-4"> {{ $t("no_more_products") }}</span>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import type { SpecialPrice } from "~/types/storyblok/storyblok";
import type { CombinedItem } from "~/types/components/category";

interface Props {
	items: CombinedItem[];
	pageSize: string;
	pictureAspectRatio: string | undefined;
	currentPage: number;
	isLoaded: boolean | undefined;
	specialPrice: SpecialPrice | null;
	showPagination?: boolean;
	categoryName?: string;
	itemCount: number;
}

const {
	items,
	pageSize,
	pictureAspectRatio,
	currentPage,
	isLoaded,
	showPagination,
	categoryName,
	itemCount
} = defineProps<Props>();

const emit = defineEmits(["getCurrentPageProducts"]);
const { productImpression } = useGtmEvents();

const isPagination = computed(
	() => showPagination && Math.ceil(itemCount / +pageSize) > 1
);

const handleGetCurrentPageProducts = (page: number) => {
	emit("getCurrentPageProducts", page);
};

onMounted(() => {
	productImpression(items, categoryName);
});

watch(
	() => items,
	() => {
		productImpression(items, categoryName);
	}
);
</script>
